/* @jsx h */
import { defineComponent, h, computed, ref, watch } from "vue";
import { useMenu } from "@app/vue/providers/index";
import { useRoute, RouterLink } from "vue-router";
import { getReservationSchedule } from "../utils";
import { DatePicker } from "./DatePicker";
import { TimeSlotPicker } from "./TimeSlotPicker";
import { daysOfWeek, formatPhone } from "@lemmonhq/orders";
import { fetchShopEvents } from "@app/server/client";
import type { EventWithShop } from "@lemmonhq/types";
import { RichTextContent, useLocalization } from "@lemmonhq/uikit";

export const TableReservationPanel = defineComponent({
  name: "TableReservationPanel",
  props: {
    hideEvents: { type: Boolean, default: false },
  },
  setup(props) {
    const { shop } = useMenu();
    const { locale, t } = useLocalization();
    const route = useRoute();
    const selectedDate = ref<string | null>(null);
    const selectedTime = ref<string | null>(null);
    const events = ref<EventWithShop[]>([]);
    const eventsLoading = ref(false);

    const shopSlug = computed(() => route.params.shopslug as string);

    // Fetch events for sidebar
    watch(
      () => shop.value,
      async (s) => {
        if (s && shopSlug.value) {
          eventsLoading.value = true;
          try {
            events.value = await fetchShopEvents(shopSlug.value);
          } catch (e) {
            console.error("Failed to load events:", e);
          } finally {
            eventsLoading.value = false;
          }
        }
      },
      { immediate: true },
    );

    // Get settings from shop
    const settings = computed(() => shop.value?.settings);
    const reservationSchedule = computed(() =>
      getReservationSchedule(settings.value),
    );

    // Helper functions to get specific settings
    const getSetting = (key: string): string => {
      const setting = settings.value?.find((s) => s.key === key);
      return typeof setting?.value === "string" ? setting.value : "";
    };

    const getNumberSetting = (key: string, defaultValue: number): number => {
      const setting = settings.value?.find((s) => s.key === key);
      const value = setting?.value;
      if (typeof value === "number") return value;
      if (typeof value === "string") {
        const parsed = parseFloat(value);
        return isNaN(parsed) ? defaultValue : parsed;
      }
      return defaultValue;
    };

    const getBooleanSetting = (key: string): boolean => {
      const setting = settings.value?.find((s) => s.key === key);
      const value = setting?.value;
      if (typeof value === "boolean") return value;
      if (value === "true" || value === "1") return true;
      return false;
    };

    // Format schedule for display
    const formatSchedule = computed(() => {
      const schedule = reservationSchedule.value;
      if (!schedule || Object.keys(schedule).length === 0) return null;

      const days = daysOfWeek;
      const dayLabels: Record<string, string> = {
        monday: t("customer.reserve.day_monday_short"),
        tuesday: t("customer.reserve.day_tuesday_short"),
        wednesday: t("customer.reserve.day_wednesday_short"),
        thursday: t("customer.reserve.day_thursday_short"),
        friday: t("customer.reserve.day_friday_short"),
        saturday: t("customer.reserve.day_saturday_short"),
        sunday: t("customer.reserve.day_sunday_short"),
      };

      return days
        .filter((day) => schedule[day] && schedule[day].length > 0)
        .map((day) => ({
          day: dayLabels[day],
          times: schedule[day].map(
            (range) => `${range.start_time} - ${range.end_time}`,
          ),
        }));
    });

    const handleDateSelect = (date: string) => {
      selectedDate.value = date;
      selectedTime.value = null; // Reset time when date changes
    };

    const handleTimeSelect = (time: string) => {
      selectedTime.value = time;
    };

    return () => (
      <div>
        {shop.value ? (
          <div class="rounded-xl border border-gray-200 bg-white divide-y divide-gray-100">
            {/* Location + Contact — first */}
            <div class="px-5 py-4 space-y-3">
              {shop.value?.address && (
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(shop.value?.geolocation?.address || shop.value.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex items-start gap-2 text-sm text-gray-600 font-light hover:text-gray-900 transition-colors"
                >
                  <span class="material-symbols-outlined text-base mt-0.5 flex-shrink-0">
                    location_on
                  </span>
                  <span>{shop.value.address}</span>
                </a>
              )}
              {shop.value?.phone && (
                <a
                  href={`tel:${shop.value.phone}`}
                  class="flex items-center gap-2 text-sm text-gray-600 font-light hover:text-gray-900 transition-colors"
                >
                  <span class="material-symbols-outlined text-base flex-shrink-0">
                    call
                  </span>
                  <span>{formatPhone(shop.value.phone)}</span>
                </a>
              )}
            </div>

            {/* Reservation Note */}
            {getSetting("reservations_note") && (
              <div class="px-4 py-3">
                <RichTextContent
                  html={getSetting("reservations_note")}
                  className="text-sm text-gray-500 font-light italic"
                />
              </div>
            )}

            {/* Reservation Hours */}
            {formatSchedule.value && formatSchedule.value.length > 0 && (
              <div class="px-4 py-3">
                <h4 class="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                  {t("customer.reserve.hours")}
                </h4>
                <div class="space-y-1">
                  {formatSchedule.value.map((item) => (
                    <div
                      key={item.day}
                      class="flex justify-between text-sm font-light"
                    >
                      <span class="text-gray-600 w-10">{item.day}</span>
                      <span class="text-gray-900">{item.times.join(", ")}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Cancellation Policy */}
            {getSetting("reservations_cancellation_policy") && (
              <div class="px-4 py-3">
                <h4 class="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">
                  {t("customer.reserve.cancellation")}
                </h4>
                <RichTextContent
                  html={getSetting("reservations_cancellation_policy")}
                  className="text-sm text-gray-600 font-light"
                />
              </div>
            )}
            {/* Upcoming Events */}
            {!props.hideEvents && events.value.length > 0 && (
              <div class="px-4 py-3">
                <div class="flex items-center justify-between mb-2">
                  <h4 class="text-xs font-medium text-gray-400 uppercase tracking-wider">
                    {t("customer.reserve.tab_events")}
                  </h4>
                  {events.value.length > 2 && (
                    <RouterLink
                      to={`/restaurant/${shopSlug.value}/reserve/events`}
                      class="text-xs text-gray-400 hover:text-gray-700"
                    >
                      {t("customer.reserve.view_all")}
                    </RouterLink>
                  )}
                </div>
                <div class="space-y-2.5">
                  {events.value.slice(0, 2).map((event) => {
                    const eventUrl = `/restaurant/${shopSlug.value}/reserve/events/${event.id}`;
                    const formatDate = (dateString: string) => {
                      const date = new Date(dateString);
                      return date.toLocaleDateString(locale.value || "en", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                      });
                    };
                    const formatTime = (time: string) =>
                      time ? time.substring(0, 5) : "";
                    return (
                      <RouterLink
                        to={eventUrl}
                        key={event.id}
                        class="flex gap-3 group"
                      >
                        {event.image && (
                          <div class="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                            <img
                              src={event.image}
                              alt={event.title}
                              class="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div class="flex-1 min-w-0">
                          <h5 class="text-sm font-medium text-gray-900 group-hover:text-gray-700 truncate">
                            {event.title}
                          </h5>
                          <div class="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                            <span class="material-symbols-outlined text-xs">
                              calendar_today
                            </span>
                            {formatDate(event.date)}
                            {event.time && (
                              <>
                                <span class="mx-0.5">·</span>
                                {formatTime(event.time)}
                              </>
                            )}
                          </div>
                        </div>
                      </RouterLink>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div class="flex items-center justify-center py-8">
            <span class="loading loading-spinner loading-md"></span>
          </div>
        )}
      </div>
    );
  },
});
